import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { ArrowRight, BookOpen, CheckCircle2, Loader2, MailX, ShieldCheck } from 'lucide-react';

const REASONS = [
  {
    value: 'not_relevant',
    label: 'The emails are not relevant to me right now',
  },
  {
    value: 'too_frequent',
    label: 'I am receiving too many emails',
  },
  {
    value: 'already_evaluated',
    label: 'I already evaluated Asha',
  },
  {
    value: 'not_in_dentistry',
    label: 'I am not involved with a dental practice',
  },
  {
    value: 'other',
    label: 'Something else',
  },
];

const getInitialEmail = (searchParams) =>
  searchParams.get('email') ||
  searchParams.get('e') ||
  searchParams.get('address') ||
  '';

const getApiBaseUrl = () => {
  const isLocalhost =
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  return process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:8001' : '');
};

const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => getInitialEmail(searchParams));
  const [reason, setReason] = useState('not_relevant');
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: 'idle', message: '' });

  const selectedReason = useMemo(
    () => REASONS.find((item) => item.value === reason) || REASONS[0],
    [reason],
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: 'idle', message: '' });

    if (!email.trim()) {
      setStatus({ type: 'error', message: 'Please enter the email address you want to unsubscribe.' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          reason,
          reasonLabel: selectedReason.label,
          details,
          source: searchParams.get('source') || 'email_footer',
          sequenceId: searchParams.get('sequenceId') || '',
          sentEmailId: searchParams.get('sentEmailId') || '',
          templateId: searchParams.get('templateId') || '',
        }),
      });

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || 'Unable to unsubscribe this email address.');
      }

      setStatus({
        type: 'success',
        message: 'You have been unsubscribed. We will stop sending educational Asha emails to this address.',
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Unable to unsubscribe this email address.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white selection:bg-blue-100 selection:text-blue-900">
      <Header />

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      <main className="pt-24 pb-12 px-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-50 via-white to-indigo-50/50"></div>
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob"></div>
          <div className="absolute top-0 -left-4 w-[500px] h-[500px] bg-purple-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-[500px] h-[500px] bg-indigo-100/40 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000"></div>
        </div>

        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-start justify-center">
            <div className="flex-1 lg:pt-4 lg:sticky lg:top-28">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white/80 px-3 py-1 text-xs font-semibold text-blue-700 shadow-sm">
                <MailX className="h-3.5 w-3.5" />
                Email preferences
              </div>

              <h1 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight text-slate-900 tracking-tight">
                Manage emails from <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                  Asha Dental
                </span>
              </h1>

              <p className="text-lg text-slate-600 mb-6 leading-relaxed max-w-xl">
                You are receiving these emails because you filled out a form asking to learn more about the Asha AI platform.
              </p>

              <div className="space-y-3 max-w-xl">
                <div className="flex gap-3 rounded-xl bg-white/70 border border-white/70 p-4 shadow-sm">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 border border-blue-100">
                    <BookOpen className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900">Educational dental AI content</h2>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      Our emails include practical videos, workflows, and plain-English guides on using AI in dentistry, from front-office automation to claims, billing, and patient communication.
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 rounded-xl bg-white/70 border border-white/70 p-4 shadow-sm">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 border border-blue-100">
                    <ShieldCheck className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900">No account required</h2>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">
                      If you unsubscribe, we will record your preference and suppress future educational outreach to this address.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full lg:w-[540px] flex-shrink-0 relative z-10">
              <Card className="shadow-2xl border-0 ring-1 ring-slate-900/5 bg-white/90 backdrop-blur-sm overflow-hidden">
                <div className="h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-600"></div>
                <CardHeader className="pb-4 pt-6 px-6">
                  <CardTitle className="text-xl font-bold text-slate-900">
                    Unsubscribe from Asha emails
                  </CardTitle>
                  <CardDescription className="text-slate-500 text-sm mt-1">
                    This only affects educational and marketing emails from Asha.
                  </CardDescription>
                </CardHeader>

                <CardContent className="px-6 pb-6">
                  {status.type === 'success' ? (
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-5">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
                        <div>
                          <h2 className="font-semibold text-emerald-950">You are unsubscribed</h2>
                          <p className="mt-1 text-sm leading-relaxed text-emerald-800">
                            {status.message}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <form className="space-y-4" onSubmit={handleSubmit}>
                      {status.type === 'error' && (
                        <Alert variant="destructive">
                          <AlertDescription>{status.message}</AlertDescription>
                        </Alert>
                      )}

                      <div className="space-y-1.5">
                        <Label htmlFor="email" className="text-xs font-semibold text-slate-700">
                          Email address *
                        </Label>
                        <Input
                          id="email"
                          type="email"
                          placeholder="you@practice.com"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          required
                          className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 transition-all h-10 text-sm"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs font-semibold text-slate-700">
                          Why are you unsubscribing?
                        </Label>
                        <RadioGroup value={reason} onValueChange={setReason} className="gap-2">
                          {REASONS.map((item) => (
                            <Label
                              key={item.value}
                              htmlFor={`reason-${item.value}`}
                              className="flex cursor-pointer items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm font-medium text-slate-700 transition-all hover:border-blue-200 hover:bg-white"
                            >
                              <RadioGroupItem id={`reason-${item.value}`} value={item.value} />
                              <span>{item.label}</span>
                            </Label>
                          ))}
                        </RadioGroup>
                      </div>

                      <div className="space-y-1.5">
                        <Label htmlFor="details" className="text-xs font-semibold text-slate-700">
                          Anything else? <span className="text-slate-400 font-normal">(Optional)</span>
                        </Label>
                        <Textarea
                          id="details"
                          placeholder="Tell us what would have made these emails more useful."
                          value={details}
                          onChange={(event) => setDetails(event.target.value)}
                          className="bg-slate-50 border-slate-200 focus:bg-white focus:border-blue-500 min-h-[88px] resize-none transition-all text-sm"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white h-10 text-base font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-blue-300 transition-all transform hover:-translate-y-0.5 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                            Unsubscribing...
                          </>
                        ) : (
                          <>
                            Unsubscribe
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </>
                        )}
                      </Button>

                      <p className="text-[10px] text-center text-slate-400 mt-3">
                        You can always reach us at sales@asha.health if this was a mistake.
                      </p>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UnsubscribePage;
